<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Quotes Dashboard" />

            <section id="filters" class="filters-section">
                <div class="card shadow-sm filters-card">
                    <div class="card-content filters-body">
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Customer</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <div class="select" style="width: 100%">
                                        <select
                                            id="search_customer"
                                            style="width: 100%"
                                            v-model="customer"
                                        >
                                            <option value="">All Customers</option>
                                            <option
                                                v-for="item in customer_items"
                                                :key="'cust_' + item"
                                                :value="item"
                                            >
                                                {{ item }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Vendor</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <span class="select">
                                        <select
                                            id="vendor_select"
                                            style="width: 100%"
                                            v-model="vendor"
                                        >
                                            <option value="">All Vendors</option>
                                            <option
                                                v-for="item in vendor_items"
                                                :key="'vend_' + item"
                                                :value="item"
                                            >
                                                {{ item }}
                                            </option>
                                        </select>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">User</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <span class="select">
                                        <select id="user_select" style="width: 100%" v-model="user">
                                            <option value="">All Users</option>
                                            <option
                                                v-for="user in user_items"
                                                :key="'user' + user.id"
                                                :value="user.id"
                                            >
                                                {{ user.name }}
                                            </option>
                                        </select>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Period</label>
                            </div>
                            <div class="field-body">
                                <div class="field has-addons">
                                    <p class="control">
                                        <span class="select">
                                            <select
                                                id="date_range"
                                                v-model="dateRange"
                                                @change="change_date($event)"
                                            >
                                                <option
                                                    disabled
                                                    hidden
                                                    value=""
                                                    style="color: grey"
                                                >
                                                    Date Presets
                                                </option>
                                                <option
                                                    v-for="(selectOption, index) in selectOptions"
                                                    :value="selectOption.value"
                                                    :key="index"
                                                >
                                                    {{ selectOption.label }}
                                                </option>
                                                <option disabled hidden value="custom">
                                                    Custom
                                                </option>
                                            </select>
                                        </span>
                                    </p>
                                    <p class="control is-expanded">
                                        <VueDatePicker
                                            v-model="range"
                                            :enable-time-picker="false"
                                            range
                                        />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="buttons is-right filters-actions">
                            <button @click="reset" class="button is-small is-white">Reset</button>
                            <div class="filter-action">
                                <button
                                    @click="ajax_get_dashboard"
                                    class="button is-info"
                                    :disabled="isSearching"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="isSearching"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading quotes"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="dashboard_quotes" v-if="queryData.length > 0">
                <div class="title_div">
                    <span class="site">Quotes</span>
                </div>
                <DashboardQuotesTable
                    :columns="dashBoardQuotesColumnsHeader"
                    :data="queryData"
                    :initial-sort="{ field: 'date_sold', direction: 'desc' }"
                />
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="$emit('download-csv', [queryDataRaw, 'QuotesDashboard', null])"
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<style scoped>
    #dashboard_quotes {
        width: 100%;
        max-width: 80vw;
        overflow-x: auto;
        margin-bottom: 50px;
        padding-bottom: 50px;
    }
    .filters-section {
        display: flex;
        justify-content: center;
    }

    .filters-card {
        width: 100%;
        max-width: 720px;
        margin: 0 auto 1.5em;
    }

    .filters-body {
        display: flex;
        flex-direction: column;
        gap: 0.75em;
    }

    .filters-actions {
        margin-top: 0.5em;
        gap: 0.75em;
    }

    .filter-action {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }

    .loading-icon {
        display: flex;
        align-items: center;
        color: #3273dc;
    }

    .rotating {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    .title_div {
        margin-top: 1.5em;
    }

    .site {
        font-size: 2em;
    }
</style>

<script>
    import moment from 'moment'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import dashBoardQuotesColumnsHeader from '@/data/DashBoardQuotesColumnsHeader'
    import { mapStores, mapWritableState } from 'pinia'
    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import { useDashboardQuotesStore } from '@/stores/dashboardQuotesStore'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import DashboardQuotesTable from '@/components/shared/DashboardQuotesTable.vue'

    export default {
        name: 'DashboardQuotes',
        components: {
            VueDatePicker,
            HeroContainer,
            DashboardQuotesTable,
        },
        computed: {
            ...mapStores(useDropdownsStore, useDashboardQuotesStore),
            ...mapWritableState(useDashboardQuotesStore, {
                customer: 'customer',
                vendor: 'vendor',
                user: 'user',
                dateRange: 'date_range',
                range: 'range',
                queryDataRaw: 'queryDataRaw',
            }),
            customer_items() {
                // For customer dropdown
                return this.dropdownsStore.customer
            },
            vendor_items() {
                // For vendor dropdown
                return this.dropdownsStore.vendor
            },
            user_items() {
                // For vendor dropdown
                return this.dropdownsStore.user
            },
            queryData() {
                let raw_query = [...this.queryDataRaw]
                return raw_query
            },
            isSearching() {
                return this.searchStore.ajax_searching > 0
            },
        },
        data: function () {
            return {
                dashBoardQuotesColumnsHeader,
                numeral: require('numeral'),
                selectOptions: [
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'prev_month', label: 'Prev Month' },
                    { value: 'this_quarter', label: 'This Quarter' },
                    { value: 'prev_quarter', label: 'Prev Quarter' },
                    { value: 'this_year', label: 'This Year' },
                    { value: 'prev_year', label: 'Prev Year' },
                    { value: 'all', label: 'All' },
                ],
            }
        },
        watch: {
            plotData: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.dashboardQuotesStore.addToFigureKey(1)
            },
        },
        methods: {
            getDates,

            reset() {
                this.dashboardQuotesStore.resetDashboardQuotesState()
            },
            change_date(event) {
                const selectedValue = event.target.value

                let range = this.getDates(selectedValue)
                if (range) {
                    this.dashboardQuotesStore.setRange(range)
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            filter_records() {
                this.ajax_get_dashboard(this.filter_customer, this.filter_user)
            },
            async ajax_get_dashboard() {
                this.period_last_filtered = this.period
                const begin_date = moment.utc(this.range[0]).format('YYYY-MM-DD')
                const end_date = moment.utc(this.range[1]).endOf('day').format('YYYY-MM-DD')
                this.set_searching(true)

                try {
                    await this.dashboardQuotesStore.fetchDashboardQuotes({
                        begin_date,
                        end_date,
                        customer: this.customer,
                        vendor: this.vendor,
                        user_id: this.user,
                    })
                } catch (e) {
                    console.error(JSON.stringify(e?.message || e))
                } finally {
                    this.set_searching(false)
                }
            },
        },
    }
</script>
